import { PricingBreakdownItem } from "../../../../util/pricing";
import { Product } from "./product";
import { Station } from "./station";
import { WorkflowStep } from "./workflow";

export enum ProductDiffChangeType {
    Removed,
    Modified,
    Added
}

export enum ProductDiffItemType {
    Workflow,
    Outsourced,
    Purchased,
    Material,
}

export interface ProductDiffItem {
    name: string,
    changeType: ProductDiffChangeType,
    itemType: ProductDiffItemType,
    priceChange: number,
    hoursChange?: number,
}
export type ProductDiffReviewItem = ProductDiffItem & { checked: boolean }

function diffList(oldList: PricingBreakdownItem[], newList: PricingBreakdownItem[],
    onAdded: (newItem: PricingBreakdownItem) => ProductDiffItem,
    onDeleted: (oldItem: PricingBreakdownItem) => ProductDiffItem,
    isDifferent: (oldItem: PricingBreakdownItem, newItem: PricingBreakdownItem) => { result: boolean, data: any },
    onModified: (oldItem: PricingBreakdownItem, newItem: PricingBreakdownItem, data: any) => ProductDiffItem,
    ): ProductDiffItem[] {
    let output: ProductDiffItem[] = [];
    // We need to store the indexes of old & new items we've already checked so we don't double count them
    let checkedNew: number[] = [];
    // Check for deleted and changed items
    for (const oldItem of oldList) {
        const indexInNew = newList.findIndex((i, index) => !checkedNew.includes(index) && i.name === oldItem.name);
        if (indexInNew != -1) {
            // Save index so we don't check for this one again
            checkedNew.push(indexInNew);
            // It exists, so we have to compare prices
            const newItem = newList[indexInNew];
            const difference = newItem.price - oldItem.price;
            // Have a small tolerance for floating point rounding errors
            const { result: different, data } = isDifferent(oldItem, newItem);
            if (different) {
                output.push(onModified(oldItem, newItem, data));
            }
        } else {
            // Doesn't exist in new - was deleted
            output.push(onDeleted(oldItem));
        }
    }
    let checkedOld: number[] = [];
    // Check for added items
    for (const newItem of newList) {
        const indexInOld = oldList.findIndex((i, index) => !checkedOld.includes(index) && i.name === newItem.name);
        if (indexInOld != -1) {
            // Save index so we don't check for this one again
            checkedOld.push(indexInOld);
        } else {
            // Doesn't exist in old - was added
            output.push(onAdded(newItem));
        }
    }
    return output.sort((a, b) => a.changeType - b.changeType);
}

export function diffProducts(oldProduct: Product, newProduct: Product, qty: number = null, stationGetter: (s: WorkflowStep) => Station = () => null): ProductDiffItem[] {
    const oldProductBreakdown = Product.getBasePricingBreakdown(oldProduct, 1, stationGetter, null, qty);
    const newProductBreakdown = Product.getBasePricingBreakdown(newProduct, 1, stationGetter, null, qty);
    let output: ProductDiffItem[] = [];
    //// Material
    // Case 1: Old product has no material, new one does - material added
    if (!oldProductBreakdown.material && newProductBreakdown.material) {
        output.push({
            name: newProductBreakdown.material.name,
            changeType: ProductDiffChangeType.Added,
            itemType: ProductDiffItemType.Material,
            // No material = 0, so this is just total material cost
            priceChange: newProductBreakdown.material.price,
            hoursChange: null,
        });
    }
    // Case 2: Old product has material, new one does not - material deleted
    else if (oldProductBreakdown.material && !newProductBreakdown.material) {
        output.push({
            name: oldProductBreakdown.material.name,
            changeType: ProductDiffChangeType.Removed,
            itemType: ProductDiffItemType.Material,
            // No material = 0, so this is just negative total material cost
            priceChange: -1 * oldProductBreakdown.material.price,
            hoursChange: null
        });
    }
    // Case 3: Both products have material, have to compare price
    else if (oldProductBreakdown.material && oldProductBreakdown.material) {
        const difference = newProductBreakdown.material.price - oldProductBreakdown.material.price;
        // Have a small tolerance for floating point rounding errors
        if (!(difference > -0.1 && difference < 0.1)) {
            output.push({
                name: newProductBreakdown.material.name,
                changeType: ProductDiffChangeType.Modified,
                itemType: ProductDiffItemType.Material,
                priceChange: difference,
                hoursChange: null
            });
        }
    }
    // If neither has material, none of the above clauses should execute
    //// Purchased Items
    const oldPurchasedItems = oldProductBreakdown.purchasedItems ?? [];
    const newPurchasedItems = newProductBreakdown.purchasedItems ?? [];
    output.push(...diffList(oldPurchasedItems, newPurchasedItems,
        // on added
        (newItem) => ({
                name: newItem.name,
                changeType: ProductDiffChangeType.Added,
                itemType: ProductDiffItemType.Purchased,
                // No material = 0, so this is just negative total material cost
                priceChange: newItem.price,
                hoursChange: null
            }),
        // on deleted
        (oldItem) => ({
                name: oldItem.name,
                changeType: ProductDiffChangeType.Removed,
                itemType: ProductDiffItemType.Purchased,
                // No material = 0, so this is just negative total material cost
                priceChange: -1 * oldItem.price,
                hoursChange: null
            }),
        // is different
        (oldItem, newItem) => {
            const priceChange = newItem.price - oldItem.price;
            return {
                result: !(priceChange > -0.1 && priceChange < 0.1),
                data: { priceChange }
            };
        },
        // on modified
        (oldItem, _newItem, data) => ({
            name: oldItem.name,
            changeType: ProductDiffChangeType.Modified,
            itemType: ProductDiffItemType.Purchased,
            priceChange: data.priceChange,
            hoursChange: null
        })
        ));
    // We need to store the indexes of old & new items we've already checked so we don't double count them
    //// Workflow
    const oldWorkflow = oldProductBreakdown.labor ?? [];
    const newWorkflow = newProductBreakdown.labor ?? [];
    output.push(...diffList(oldWorkflow, newWorkflow,
        // on added
        (newStep) => {
            let newHours = newStep.subitems.find(subitem => subitem.id === 'TOTAL_HOURS')?.value ?? 0;
            return {
                name: newStep.name,
                changeType: ProductDiffChangeType.Added,
                itemType: ProductDiffItemType.Workflow,
                // No material = 0, so this is just negative total material cost
                priceChange: newStep.price,
                hoursChange: newHours
            };
        },
        // on deleted
        (oldStep) => {
            let oldHours = oldStep.subitems.find(subitem => subitem.id === 'TOTAL_HOURS')?.value ?? 0;
            return {
                name: oldStep.name,
                changeType: ProductDiffChangeType.Removed,
                itemType: ProductDiffItemType.Workflow,
                // No material = 0, so this is just negative total material cost
                priceChange: -1 * oldStep.price,
                hoursChange: -1 * oldHours
            };
        },
        // is different
        (oldStep, newStep) => {
            let oldHours = oldStep.subitems.find(subitem => subitem.id === 'TOTAL_HOURS')?.value ?? 0;
            let newHours = newStep.subitems.find(subitem => subitem.id === 'TOTAL_HOURS')?.value ?? 0;
            const priceChange = newStep.price - oldStep.price;
            const hoursChange = newHours - oldHours;
            return {
                result: !(priceChange > -0.1 && priceChange < 0.1) || !(hoursChange > -0.01 && hoursChange < 0.01),
                data: { priceChange, hoursChange }
            };
        },
        // on modified
        (oldStep, _newStep, data) => ({
            name: oldStep.name,
            changeType: ProductDiffChangeType.Modified,
            itemType: ProductDiffItemType.Workflow,
            priceChange: data.priceChange,
            hoursChange: data.hoursChange
        })
        ));    // We need to store the indexes of old & new items we've already checked so we don't double count them
    //// Outsourced
    const oldOutsourced = oldProductBreakdown.process ?? [];
    const newOutsourced = newProductBreakdown.process ?? [];
    output.push(...diffList(oldOutsourced, newOutsourced,
        // on added
        (newItem) => ({
                name: newItem.name,
                changeType: ProductDiffChangeType.Added,
                itemType: ProductDiffItemType.Outsourced,
                // No material = 0, so this is just negative total material cost
                priceChange: newItem.price,
                hoursChange: null
            }),
        // on deleted
        (oldItem) => ({
                name: oldItem.name,
                changeType: ProductDiffChangeType.Removed,
                itemType: ProductDiffItemType.Outsourced,
                // No material = 0, so this is just negative total material cost
                priceChange: -1 * oldItem.price,
                hoursChange: null
            }),
        // is different
        (oldItem, newItem) => {
            const priceChange = newItem.price - oldItem.price;
            return {
                result: !(priceChange > -0.1 && priceChange < 0.1),
                data: { priceChange }
            };
        },
        // on modified
        (oldItem, _newItem, data) => ({
            name: oldItem.name,
            changeType: ProductDiffChangeType.Modified,
            itemType: ProductDiffItemType.Outsourced,
            priceChange: data.priceChange,
            hoursChange: null
        })
        ));

    return output.sort((a, b) => a.changeType - b.changeType);
}
